import React, { useState, useMemo, useEffect } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    Legend,
} from "recharts";
import "./TeamPerformance.scss";
import DateFilterSelect from "../../../../../CustomDatePicker/DateFilterSelectold";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { FILTER_ACTION } from '../../../../../../reducers/CommonFilters.reducer';
import { getManagerEmployeeData } from "../actioncreator";
import { TENANT_ID } from "../../../../../../utils/api-factory";

const API_BASE_URL = "https://finance.infitouch.in";

const fetchDashboardData = async (
    dateRange,selectedMemeber,
    filters = {},
    signal,
    employeeEmail = "",
) => {
    const defaultFilters = {
        baseLeadsPerDay: 1000,
        workingDays: 26,
        monthlyGrowthRate: 0.1,
        approvalRateFresh: 0.1,
        disbursalRateFresh: 0.5,
        disbursalRateRepeat: 0.8,
        avgLoanAmountFresh: 5000,
        avgLoanAmountRepeat: 8000,
        managerRatio: 0.1,
        creditTeamRatio: 0.2,
        customerCompletionRate: 0.3,
    };

    const requestBody = {
        startDate: dateRange[0],
        endDate: dateRange[1],
        employeeEmail:selectedMemeber,
        ...(employeeEmail ? { employeeEmail } : {}),
        ...defaultFilters,
        ...filters,
    };

    try {
        const response = await fetch(`${API_BASE_URL}/business-sheet/dashboard`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Tenant-ID": TENANT_ID,
            },
            body: JSON.stringify(requestBody),
            signal,
        });

        const json = await response.json().catch(() => null);

        if (!response.ok) {
            const message =
                json?.data || json?.message || `Request failed (${response.status})`;
            return { error: true, message };
        }

        if (!Array.isArray(json)) return [];

        const selectedMonthNames = [
            "JAN",
            "FEB",
            "MAR",
            "APR",
            "MAY",
            "JUN",
            "JUL",
            "AUG",
            "SEP",
            "OCT",
            "NOV",
            "DEC",
        ];
        const startDate = new Date(dateRange[0]);
        const endDate = new Date(dateRange[1]);
        const fromMonthIdx = startDate.getMonth();
        const toMonthIdx = endDate.getMonth();

        return json.filter((item) => {
            const monthKey = String(item?.month || "")
                .trim()
                .toUpperCase()
                .slice(0, 3);
            const idx = selectedMonthNames.indexOf(monthKey);
            return idx >= fromMonthIdx && idx <= toMonthIdx;
        });
    } catch (networkError) {
        if (networkError?.name === "AbortError") return null;
        return {
            error: true,
            message: "Network error — please check your connection.",
        };
    }
};

const fetchSummaryData = async (dateRange,selectedMemeber, filters = {}) => {
    const defaultFilters = {
        baseLeadsPerDay: 1000,
        workingDays: 26,
        monthlyGrowthRate: 0.1,
        approvalRateFresh: 0.1,
        disbursalRateFresh: 0.5,
        disbursalRateRepeat: 0.8,
        avgLoanAmountFresh: 5000,
        avgLoanAmountRepeat: 8000,
        managerRatio: 0.1,
        creditTeamRatio: 0.2,
    };

    const requestBody = {
        startDate: dateRange[0],
        endDate: dateRange[1],
        employeeEmail:selectedMemeber,
        ...defaultFilters,
        ...filters,
    };
    const res = await fetch(`${API_BASE_URL}/business-sheet/summary`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Tenant-ID": TENANT_ID,
        },
        body: JSON.stringify(requestBody),
    });

    const json = await res.json().catch(() => null);

    if (!res.ok) {
        const message =
            json?.data || json?.message || `Request failed (${res.status})`;
        return { error: true, message };
    }

    return json;
};

const ALLOCATION_CONFIG = {
    customerAllocation: {
        title: "Customer Service",
        subtitleKey: "totalLeads",
        subtitleLabel: "leads",
        managerCountLabel: "leads",
        employeeCountLabel: "leads",
        pendingLeadsLabel: "pendingLeads",
        theme: "blue",
        type: "",
        completedKey: "completedLeads",
        completedLabel: "Completed Leads"
    },

    creditFreshAllocation: {
        title: "Credit Fresh",
        subtitleKey: "totalApprovals",
        subtitleLabel: "approvals",
        managerCountLabel: "approvals",
        employeeCountLabel: "approvals",
        pendingLeadsLabel: "pendingLeads",
        theme: "green",
        type: "",
        completedKey: "completedLeads",
        completedLabel: "Completed Leads"
    },

    creditRepeatAllocation: {
        title: "Credit Repeat",
        subtitleKey: "totalApprovals",
        subtitleLabel: "approvals",
        managerCountLabel: "approvals",
        employeeCountLabel: "approvals",
        pendingLeadsLabel: "pendingLeads",
        theme: "purple",
        type: "",
        completedKey: "completedLeads",
        completedLabel: "Completed Leads"
    },

    operationsAllocation: {
        title: "Operations",
        subtitleKey: "totalLeads",
        subtitleLabel: "operations",
        managerCountLabel: "operations",
        employeeCountLabel: "operations",
        pendingLeadsLabel: "pendingLeads",
        theme: "orange",
        type: "",
        completedKey: "disbursedLeads",
        completedLabel: "Disbursed Leads"
    },

    creditAllocation: {
        title: "Credit",
        subtitleKey: "totalApprovals",
        subtitleLabel: "approvals",
        managerCountLabel: "approvals",
        employeeCountLabel: "approvals",
        pendingLeadsLabel: "pendingLeads",
        theme: "green",
        type: "approved",
        completedKey: "completedLeads",
        completedLabel: "Approved Leads"
    },
};

const AllocationSection = ({ allocationKey, allocationData }) => {
    if (!allocationData) return null;

    const config = ALLOCATION_CONFIG[allocationKey];

    if (!config) return null;

    let totalLeads = 0;
    let totalCompletedCount = 0;
    (allocationData?.managers || []).forEach(mgr => {
        (mgr?.employees || []).forEach(emp => {
            totalLeads += Number(emp?.assignedLeads || 0);
            if (allocationKey === "customerAllocation") {
                totalCompletedCount += Number(emp?.completedLeads || 0);
            } else if (allocationKey === "operationsAllocation") {
                totalCompletedCount += Number(emp?.disbursedLeads || 0);
            } else if (allocationKey === "creditAllocation" || allocationKey === "creditFreshAllocation" || allocationKey === "creditRepeatAllocation") {
                totalCompletedCount += Number(emp?.completedLeads || 0);
            } else {
                const key = config.completedKey || "completedLeads";
                totalCompletedCount += Number(emp?.[key] || 0);
            }
        });
    });

    let subtitleLabel = "completed";
    if (allocationKey === "operationsAllocation") {
        subtitleLabel = "operations";
    } else if (allocationKey === "creditAllocation" || allocationKey === "creditFreshAllocation" || allocationKey === "creditRepeatAllocation") {
        subtitleLabel = "approvals";
    }

    return (
        <div className="tp__alloc-col">
            <div className={`tp__alloc-hd tp__alloc-hd--${config.theme}`}>
                <span className="tp__alloc-title">{config.title}</span>

                <span className="tp__alloc-sub">
                    {allocationData?.[config.subtitleKey] || allocationData?.totalLeads || allocationData?.totalOperations || 0} leads
                    {" · "}
                    {totalCompletedCount} {subtitleLabel}
                    {" · "}
                    {allocationData?.totalManagers || 0} manager{(allocationData?.totalManagers || 0) !== 1 ? "s" : ""}
                </span>
            </div>
            {console.log("emp mgr :",config)}

            {allocationData?.managers?.map((mgr) => (
                <div key={mgr.managerEmail} className="tp__alloc-mgr">
                    <div className="tp__alloc-mgr-row">
                        <div className={`tp__alloc-av tp__alloc-av--${config.theme}`}>
                            {getInitials(mgr.managerName)}
                        </div>

                        <div className="tp__alloc-mgr-info">
                            <span className="name">{mgr.managerName?.trim()}</span>

                            <span className="email">{mgr.managerEmail}</span>
                        </div>

                        <div className="tp__alloc-mgr-meta">
                            <span className="meta">{mgr?.totalEmployees ?? 0} employees</span>
                        </div>

                        {/* <span className="tp__alloc-mgr-count">
              {mgr.totalLeadsAssigned} {config.managerCountLabel}
            </span> */}
                    </div>

                    {mgr?.employees?.map((emp) => (
                        <div key={emp.employeeEmail} className="tp__alloc-emp-row">
                            <div
                                className={`tp__alloc-av tp__alloc-av--sm tp__alloc-av--sm tp__alloc-av--${config.theme}-lt`}
                            >
                                {getInitials(emp.employeeName)}
                            </div>

                            <div className="tp__alloc-emp-info">
                                <span className="name">{emp.employeeName?.trim()}</span>

                                <span className="email">{emp.employeeEmail}</span>
                            </div>
                            <div className="tp__alloc-emp-counts">
                                <span className="tp__alloc-emp-count" data-tip="Projected Leads"
                                    style={{ color: "#3B82F6" }}>
                                    {emp?.projectedLeads ?? 0}
                                </span>
                                <span className="tp__alloc-emp-count">|</span>

                                <span className="tp__alloc-emp-count" data-tip="Assigned Leads"
                                    style={{ color: "#8B5CF6" }}>
                                    {emp?.assignedLeads ?? 0}
                                </span>

                                <span className="tp__alloc-emp-count">|</span>
                                <span
                                    className="tp__alloc-emp-count tp__alloc-emp-count--ok"
                                    data-tip={config?.completedLabel}
                                    style={{ color: "#22C55E" }}
                                >
                                    {emp?.[config?.completedKey || "completedLeads"] ?? 0}
                                </span>
                                <span className="tp__alloc-emp-count">|</span>
                                <span
                                    className="tp__alloc-emp-count tp__alloc-emp-count--warn"
                                    data-tip="Pending Leads"
                                    style={{ color: "#F59E0B" }}
                                >
                                    {emp?.pendingLeads ?? 0}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

const getInitials = (name) => {
    const safe = String(name || "").trim();
    if (!safe) return "?";
    return safe
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
};

const AVATAR_COLORS = ["blue", "purple", "slate", "rose", "teal", "amber"];

const formatINR = (val) => "₹" + Number(val).toLocaleString("en-IN");

function deriveMembers(data) {
    const map = {};
    data.forEach((d) => {
        const customerManagers = d?.customerAllocation?.managers || [];
        const creditManagers = d?.creditAllocation?.managers || [];
        const operationsManagers = d?.operationsAllocation?.managers || [];

        customerManagers.forEach((mgr) => {
            (mgr?.employees || []).forEach((emp) => {
                const email = String(emp?.employeeEmail || "").trim();
                if (!email) return;
                if (!map[email]) {
                    map[email] = {
                        name: String(emp?.employeeName || "").trim() || "Unknown",
                        email,
                        badge: "Customer",
                        managerName: String(mgr?.managerName || "").trim() || "—",
                        leads: 0,
                        completed: 0,
                        pending: 0,
                        projectedLeads: 0,
                        disbursedLeads: 0,
                    };
                }
                map[email].leads += Number(emp?.assignedLeads || 0);
                map[email].completed += Number(emp?.completedLeads || 0);
                map[email].pending += Number(emp?.pendingLeads || 0);
                map[email].projectedLeads += Number(emp?.projectedLeads || 0);
                map[email].disbursedLeads += Number(emp?.disbursedLeads || 0);
            });
        });

        creditManagers.forEach((mgr) => {
            (mgr?.employees || []).forEach((emp) => {
                const email = String(emp?.employeeEmail || "").trim();
                if (!email) return;
                if (!map[email]) {
                    map[email] = {
                        name: String(emp?.employeeName || "").trim() || "Unknown",
                        email,
                        badge: "Credit",
                        managerName: String(mgr?.managerName || "").trim() || "—",
                        leads: 0,
                        completed: 0,
                        pending: 0,
                        projectedLeads: 0,
                        disbursedLeads: 0,
                    };
                }
                map[email].leads += Number(emp?.assignedLeads || 0);
                map[email].completed += Number(emp?.completedLeads || 0);
                map[email].pending += Number(emp?.pendingLeads || 0);
                map[email].projectedLeads += Number(emp?.projectedLeads || 0);
                map[email].disbursedLeads += Number(emp?.disbursedLeads || 0);
            });
        });

        operationsManagers.forEach((mgr) => {
            (mgr?.employees || []).forEach((emp) => {
                const email = String(emp?.employeeEmail || "").trim();
                if (!email) return;
                if (!map[email]) {
                    map[email] = {
                        name: String(emp?.employeeName || "").trim() || "Unknown",
                        email,
                        badge: "Operations",
                        managerName: String(mgr?.managerName || "").trim() || "—",
                        leads: 0,
                        completed: 0,
                        pending: 0,
                        projectedLeads: 0,
                        disbursedLeads: 0,
                    };
                }
                map[email].leads += Number(emp?.assignedLeads || 0);
                map[email].completed += Number(emp?.completedLeads || 0);
                map[email].pending += Number(emp?.pendingLeads || 0);
                map[email].projectedLeads += Number(emp?.projectedLeads || 0);
                map[email].disbursedLeads += Number(emp?.disbursedLeads || 0);
            });
        });
    });
    const list = Object.values(map).sort((a, b) => b.leads - a.leads);
    return list.map((m, i) => {
        let pct = 0;
        if (m.leads > 0) {
            if (m.badge === "Operations") {
                pct = (m.disbursedLeads / m.leads) * 100;
            } else {
                pct = (m.completed / m.leads) * 100;
            }
        }
        return {
            ...m,
            initials: getInitials(m.name),
            color: AVATAR_COLORS[i % AVATAR_COLORS.length],
            barPct: pct.toFixed(2),
        };
    });
}

const MONTH_LABEL = {
    JAN: "Jan",
    FEB: "Feb",
    MAR: "Mar",
    APR: "Apr",
    MAY: "May",
    JUN: "Jun",
    JUL: "Jul",
    AUG: "Aug",
    SEP: "Sep",
    OCT: "Oct",
    NOV: "Nov",
    DEC: "Dec",
};

const normalizeMonth = (month) =>
    String(month || "")
        .trim()
        .toUpperCase()
        .slice(0, 3);
const monthLabel = (month) =>
    MONTH_LABEL[normalizeMonth(month)] || String(month || "");

const CalendarIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const FilterIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);
const RefreshIcon = () => (
    <svg
        style={{ width: "auto" }}
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
);
const TrendIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
    >
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
    </svg>
);
const UsersIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);
const CheckIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);
const RupeeIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <path d="M6 3h12M6 8h12M6 13l8.5 8" />
        <path d="M6 8a5 5 0 0 0 0 5h3" />
    </svg>
);
const ChevronIcon = ({ open }) => (
    <svg
        style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform .2s ease",
        }}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const SlidersIcon = () => (
    <svg
        style={{ width: "auto" }}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" />
        <line x1="9" y1="8" x2="15" y2="8" />
        <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
);

const ADVANCED_FILTER_CONFIG = [
    {
        key: "baseLeadsPerDay",
        label: "Base Leads / Day",
        type: "number",
        step: 1,
    },
    { key: "workingDays", label: "Working Days", type: "number", step: 1 },
    {
        key: "monthlyGrowthRate",
        label: "Monthly Growth Rate",
        type: "number",
        step: 0.01,
    },
    {
        key: "approvalRateFresh",
        label: "Approval Rate (Fresh)",
        type: "number",
        step: 0.01,
    },
    {
        key: "disbursalRateFresh",
        label: "Disbursal Rate (Fresh)",
        type: "number",
        step: 0.01,
    },
    {
        key: "disbursalRateRepeat",
        label: "Disbursal Rate (Repeat)",
        type: "number",
        step: 0.01,
    },
    {
        key: "avgLoanAmountFresh",
        label: "Avg Loan Amount (Fresh)",
        type: "number",
        step: 100,
    },
    {
        key: "avgLoanAmountRepeat",
        label: "Avg Loan Amount (Repeat)",
        type: "number",
        step: 100,
    },
    {
        key: "customerCompletionRate",
        label: "Customer Completion Rate",
        type: "number",
        step: 0.01,
    },
];

const DEFAULT_ADVANCED_FILTERS = {
    baseLeadsPerDay: 1000,
    workingDays: 26,
    monthlyGrowthRate: 0.1,
    approvalRateFresh: 0.1,
    disbursalRateFresh: 0.5,
    disbursalRateRepeat: 0.8,
    avgLoanAmountFresh: 5000,
    avgLoanAmountRepeat: 8000,
    customerCompletionRate: 0.3,
};

const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "10px 14px",
                boxShadow: "0 4px 12px rgba(0,0,0,.1)",
                fontSize: 12,
            }}
        >
            <p style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>
                {label}
            </p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.fill, marginBottom: 2 }}>
                    {p.name}: <strong>{p.value}</strong>
                </p>
            ))}
        </div>
    );
};
const AreaTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const get = (key) => payload.find((p) => p.dataKey === key)?.value ?? 0;

    const freshAmt = get("projectedFreshDisbursedAmount");
    const repeatAmt = get("projectedRepeatDisbursedAmount");
    const totalAmt = get("projectedTotalDisbursedAmount");
    const actualfreshAmt = get("actualFreshDisbursedAmount");
    const actualrepeatAmt = get("actualRepeatDisbursedAmount");
    const actualtotalAmt = get("actualTotalDisbursedAmount");

    return (
        <>
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "10px 14px",
                    boxShadow: "0 4px 12px rgba(0,0,0,.1)",
                    fontSize: 12,
                }}
            >
                <p style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>
                    {label}
                </p>

                <p style={{ color: "#f59e0b", marginBottom: 2 }}>
                    Projected Total: <strong>{formatINR(totalAmt)}</strong>
                    <span style={{ color: "#9ca3af", marginLeft: 6 }}>

                    </span>
                </p>
                <p style={{ color: "#4f6ef7", marginBottom: 2 }}>
                    Projected Fresh: <strong>{formatINR(freshAmt)}</strong>
                    <span style={{ color: "#9ca3af", marginLeft: 6 }}>

                    </span>
                </p>
                <p style={{ color: "#10b981", marginBottom: 0 }}>
                    Projected Repeat: <strong>{formatINR(repeatAmt)}</strong>
                    <span style={{ color: "#9ca3af", marginLeft: 6 }}>

                    </span>
                </p>
                <p style={{ color: "#14b8a6", marginBottom: 2 }}>
                    Actual Total: <strong>{formatINR(actualtotalAmt)}</strong>
                    <span style={{ color: "#9ca3af", marginLeft: 6 }}>

                    </span>
                </p>
                <p style={{ color: "#7c3aed", marginBottom: 2 }}>
                    Actual Fresh: <strong>{formatINR(actualfreshAmt)}</strong>
                    <span style={{ color: "#9ca3af", marginLeft: 6 }}>

                    </span>
                </p>
                <p style={{ color: "#ec4899", marginBottom: 0 }}>
                    Actual Repeat: <strong>{formatINR(actualrepeatAmt)}</strong>
                    <span style={{ color: "#9ca3af", marginLeft: 6 }}>

                    </span>
                </p>
            </div>
        </>
    );
};

const TeamPerformance = () => {
    const dispatch = useDispatch();
    const { fromDate, toDate } = useSelector(state => state.FilterReduce.commonFilters);
    const [commonFilter, setCommonfilter] = useState({
        fromDate: fromDate,
        toDate: toDate

    });
    const [apiData, setApiData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [memberName,setMemberName] = useState([])
    const [selectedMemeber,setselectedMemeber] =useState("")
    // const [summaryData, setSummaryData] = useState([
    //     {
    //         month: "APR",
    //         projectedTotalLeads: 2600,
    //         actualTotalLeads: 62997,
    //         projectedFreshDisbursedCount: 260,
    //         projectedRepeatDisbursedCount: 208,
    //         projectedTotalDisbursedCount: 468,
    //         projectedFreshDisbursedAmount: 2600000.0,
    //         projectedRepeatDisbursedAmount: 3120000.0,
    //         projectedTotalDisbursedAmount: 5720000.0,
    //         actualFreshDisbursedCount: 1043,
    //         actualRepeatDisbursedCount: 797,
    //         actualTotalDisbursedCount: 1840,
    //         actualFreshDisbursedAmount: 1.1192391e7,
    //         actualRepeatDisbursedAmount: 9904738.0,
    //         actualTotalDisbursedAmount: 2.1097129e7,
    //     },
    //     {
    //         month: "MAY",
    //         projectedTotalLeads: 2860,
    //         actualTotalLeads: 24655,
    //         projectedFreshDisbursedCount: 286,
    //         projectedRepeatDisbursedCount: 229,
    //         projectedTotalDisbursedCount: 515,
    //         projectedFreshDisbursedAmount: 2860000.0,
    //         projectedRepeatDisbursedAmount: 3435000.0,
    //         projectedTotalDisbursedAmount: 6295000.0,
    //         actualFreshDisbursedCount: 115,
    //         actualRepeatDisbursedCount: 160,
    //         actualTotalDisbursedCount: 275,
    //         actualFreshDisbursedAmount: 1013318.0,
    //         actualRepeatDisbursedAmount: 2029499.0,
    //         actualTotalDisbursedAmount: 3042817.0,
    //     },
    // ]);
    const [summaryData, setSummaryData] = useState([]);

    console.log("summaryData", summaryData);

    // Advanced filters state
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [advancedDraft, setAdvancedDraft] = useState({
        ...DEFAULT_ADVANCED_FILTERS,
    });
    const [appliedAdvanced, setAppliedAdvanced] = useState({
        ...DEFAULT_ADVANCED_FILTERS,
    });

    useEffect(() => {
        setCommonfilter({
            fromDate: fromDate,
            toDate: toDate
        });

        getManagerEmployeeData(callback=>{
            setMemberName(callback ?? [])
        })
    }, [fromDate, toDate]);

    const [activeTab, setActiveTab] = useState("members");
    const [openMonth, setOpenMonth] = useState(null);

    useEffect(() => {
        const controller = new AbortController();

        const fetchData = async () => {
            setLoading(true);
            const dateRange = [commonFilter.fromDate, commonFilter.toDate];
            const response = await fetchDashboardData(
                dateRange,selectedMemeber,
                appliedAdvanced,
                controller.signal,
            );

            if (controller.signal.aborted) return;

            setApiData(response);
            // setApiData([
            //     {
            //         month: "MAY",
            //         totalLeadsPerDay: 100,
            //         totalApprovalsPerDay: 10,
            //         freshDisbursedPerDay: 5,
            //         repeatDisbursedPerDay: 4,
            //         freshAmount: 25000.0,
            //         repeatAmount: 32000.0,
            //         totalAmount: 57000.0,
            //         actualDisbursalData: {
            //             totalDisbursedAmount: 3042817.0,
            //             repeatDisbursedAmount: 2029499.0,
            //             freshDisbursedAmount: 1013318.0,
            //             totalDisbursedCount: 275,
            //             repeatDisbursedCount: 160,
            //             freshDisbursedCount: 115,
            //         },
            //         customerAllocation: {
            //             totalLeads: 100,
            //             totalManagers: 1,
            //             managers: [
            //                 {
            //                     managerName: "Fahad",
            //                     managerEmail: "fahad@credfin.money",
            //                     totalLeadsAssigned: 100,
            //                     totalEmployees: 4,
            //                     employees: [
            //                         {
            //                             employeeName: "sonam ",
            //                             employeeEmail: "sonam@credfin.money",
            //                             projectedLeads: 25,
            //                             assignedLeads: 800,
            //                             completedLeads: 253,
            //                             pendingLeads: 547,
            //                         },
            //                         {
            //                             employeeName: "Kunal Bhati",
            //                             employeeEmail: "Kunalbhati@credfin.money",
            //                             projectedLeads: 25,
            //                             assignedLeads: 796,
            //                             completedLeads: 219,
            //                             pendingLeads: 577,
            //                         },
            //                         {
            //                             employeeName: "Anish Kumar Patel",
            //                             employeeEmail: "Anish@credfin.money",
            //                             projectedLeads: 25,
            //                             assignedLeads: 661,
            //                             completedLeads: 136,
            //                             pendingLeads: 525,
            //                         },
            //                         {
            //                             employeeName: "Shakshi Kaushik",
            //                             employeeEmail: "Shakshi@credfin.money",
            //                             projectedLeads: 25,
            //                             assignedLeads: 35,
            //                             completedLeads: 8,
            //                             pendingLeads: 27,
            //                         },
            //                     ],
            //                 },
            //             ],
            //         },
            //         creditAllocation: {
            //             totalApprovals: 10,
            //             totalManagers: 1,
            //             managers: [
            //                 {
            //                     managerName: "Mayank",
            //                     managerEmail: "Mayank@credfin.money",
            //                     totalLeadsAssigned: 10,
            //                     totalEmployees: 2,
            //                     employees: [
            //                         {
            //                             employeeName: "Sandeep",
            //                             employeeEmail: "Sandeep@credfin.money",
            //                             projectedLeads: 5,
            //                             assignedLeads: 293,
            //                             completedLeads: 4,
            //                             pendingLeads: 289,
            //                         },
            //                         {
            //                             employeeName: "Mamta Bhardwaj ",
            //                             employeeEmail: "mamtaBhardwaj@credfin.money",
            //                             projectedLeads: 5,
            //                             assignedLeads: 303,
            //                             completedLeads: 7,
            //                             pendingLeads: 296,
            //                         },
            //                     ],
            //                 },
            //             ],
            //         },
            //     },
            // ]);
            setLoading(false);
        };

        const fetchSummary = async () => {
            const dateRange = [commonFilter.fromDate, commonFilter.toDate];
            const response = await fetchSummaryData(dateRange,selectedMemeber, appliedAdvanced);
            setSummaryData(response)
        };
        

        if (commonFilter.fromDate && commonFilter.toDate) {
            fetchData();
            fetchSummary(); // Using hardcoded data for now
        }

        return () => controller.abort();
    }, [commonFilter.fromDate, commonFilter.toDate, appliedAdvanced, selectedMemeber]);

    const handleDateFilterPopup = (type, value) => {
        dispatch({
            type: FILTER_ACTION,
            payload: { [type]: moment(value).format("YYYY-MM-DD") },
        });
        setCommonfilter((prev) => ({
            ...prev,
            [type]: moment(value).format("YYYY-MM-DD"),
        }));
    };

    const handleAdvancedChange = (key, value) => {
        setAdvancedDraft((prev) => ({
            ...prev,
            [key]: value === "" ? "" : Number(value),
        }));
    };

    const handleApplyAdvanced = () => {
        setAppliedAdvanced({ ...advancedDraft });
        setShowAdvanced(false);
    };

    const handleResetAdvanced = () => {
        setAdvancedDraft({ ...DEFAULT_ADVANCED_FILTERS });
        setAppliedAdvanced({ ...DEFAULT_ADVANCED_FILTERS });
    };

    const isAdvancedModified =
        JSON.stringify(appliedAdvanced) !==
        JSON.stringify(DEFAULT_ADVANCED_FILTERS);

    //   const totals = useMemo(() => {
    //     if (summaryData.error || !Array.isArray(summaryData) || summaryData.length === 0) {
    //       return {
    //         totalLeads: 0,
    //         totalApproval: 0,
    //         totalFresh: 0,
    //         totalRepeat: 0,
    //         totalAmount: 0,
    //       };
    //     }
    //     const totalLeads = summaryData.reduce(
    //       (s, d) => s + Number(d?.totalLeadsPerDay || 0),
    //       0,
    //     );
    //     const totalLeadsActual = summaryData.reduce(
    //       (s, d) => s + Number(d?.actualDisbursalData?.totalDisbursedCount || 0),
    //       0,
    //     );
    //     const totalApproval = summaryData.reduce(
    //       (s, d) => s + Number(d?.creditAllocation?.totalApprovals || 0),
    //       0,
    //     );
    //     const totalFresh = summaryData.reduce(
    //       (s, d) => s + Number(d?.freshDisbursedPerDay || 0),
    //       0,
    //     );
    //     const totalRepeat = summaryData.reduce(
    //       (s, d) => s + Number(d?.repeatDisbursedPerDay || 0),
    //       0,
    //     );
    //     const totalAmount = summaryData.reduce(
    //       (s, d) => s + Number(d?.totalAmount || 0),
    //       0,
    //     );
    //     return { totalLeads, totalApproval, totalFresh, totalRepeat, totalAmount };
    //   }, [summaryData]);

    const totals = useMemo(() => {
        if (!Array.isArray(summaryData) || summaryData.length === 0) {
            return {
                projectedFreshDisbursed: 0,
                projectedRepeatDisbursed: 0,
                projectedTotalDisbursed: 0,
                actualFreshDisbursed: 0,
                actualRepeatDisbursed: 0,
                actualTotalDisbursed: 0,
            };
        }

        const projectedFreshDisbursed = summaryData.reduce(
            (sum, item) => sum + Number(item?.projectedFreshDisbursedCount || 0),
            0,
        );

        const projectedRepeatDisbursed = summaryData.reduce(
            (sum, item) => sum + Number(item?.projectedRepeatDisbursedCount || 0),
            0,
        );

        const projectedTotalDisbursed = summaryData.reduce(
            (sum, item) => sum + Number(item?.projectedTotalDisbursedCount || 0),
            0,
        );

        const actualFreshDisbursed = summaryData.reduce(
            (sum, item) => sum + Number(item?.actualFreshDisbursedCount || 0),
            0,
        );

        const actualRepeatDisbursed = summaryData.reduce(
            (sum, item) => sum + Number(item?.actualRepeatDisbursedCount || 0),
            0,
        );

        const actualTotalDisbursed = summaryData.reduce(
            (sum, item) => sum + Number(item?.actualTotalDisbursedCount || 0),
            0,
        );

        return {
            projectedFreshDisbursed,
            projectedRepeatDisbursed,
            projectedTotalDisbursed,
            actualFreshDisbursed,
            actualRepeatDisbursed,
            actualTotalDisbursed,
        };
    }, [summaryData]);

    //   const chartData = useMemo(() => {
    //     if (summaryData.error || !Array.isArray(summaryData)) return [];
    //     return summaryData?.map((d) => ({
    //       month: monthLabel(d?.month),
    //       Leads: Number(d?.totalLeadsPerDay || 0),
    //       Approvals: Number(d?.totalApprovalsPerDay || 0),
    //       amount: Number(d?.totalAmount || 0),
    //       TotalDisburseamount: Number(
    //         d?.actualDisbursalData?.totalDisbursedAmount || 0,
    //       ),
    //       RepeatDisburseamount: Number(
    //         d?.actualDisbursalData?.repeatDisbursedAmount || 0,
    //       ),
    //       FreshDisburseamount: Number(
    //         d?.actualDisbursalData?.freshDisbursedAmount || 0,
    //       ),
    //       TotalDisbursecount: Number(
    //         d?.actualDisbursalData?.totalDisbursedCount || 0,
    //       ),
    //       RepeatDisbursecount: Number(
    //         d?.actualDisbursalData?.repeatDisbursedCount || 0,
    //       ),
    //       FreshDisbursecount: Number(
    //         d?.actualDisbursalData?.freshDisbursedCount || 0,
    //       ),
    //     }));
    //   }, [summaryData]);

    const chartData = useMemo(() => {
        if (!Array.isArray(summaryData)) return [];

        return summaryData?.map((d) => ({
            month: monthLabel(d?.month),

            // Projected
            projectedFreshDisbursed: Number(d?.projectedFreshDisbursedCount || 0),

            projectedRepeatDisbursed: Number(d?.projectedRepeatDisbursedCount || 0),

            projectedTotalDisbursed: Number(d?.projectedTotalDisbursedCount || 0),

            projectedFreshDisbursedAmount: Number(d?.projectedFreshDisbursedAmount || 0),

            projectedRepeatDisbursedAmount: Number(d?.projectedRepeatDisbursedAmount || 0),

            projectedTotalDisbursedAmount: Number(d?.projectedTotalDisbursedAmount || 0),

            // Actual
            actualFreshDisbursed: Number(d?.actualFreshDisbursedCount || 0),

            actualRepeatDisbursed: Number(d?.actualRepeatDisbursedCount || 0),

            actualTotalDisbursed: Number(d?.actualTotalDisbursedCount || 0),

            actualFreshDisbursedAmount: Number(d?.actualFreshDisbursedAmount || 0),

            actualRepeatDisbursedAmount: Number(d?.actualRepeatDisbursedAmount || 0),

            actualTotalDisbursedAmount: Number(d?.actualTotalDisbursedAmount || 0),

            // Amounts (optional)
            projectedTotalAmount: Number(d?.projectedTotalDisbursedAmount || 0),

            actualTotalAmount: Number(d?.actualTotalDisbursedAmount || 0),
        }));
    }, [summaryData]);

    const members = useMemo(() => {
        if (apiData.error || !Array.isArray(apiData)) return [];
        return deriveMembers(apiData);
    }, [apiData]);

    if (!apiData) return null;

    if (loading) {
        return (
            <div
                className="MainTeamsData"
                style={{
                    marginTop: "50px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "50vh",
                }}
            >
                <div style={{ textAlign: "center" }}>
                    <div
                        style={{ fontSize: "18px", color: "#6b7280", marginBottom: "10px" }}
                    >
                        Loading dashboard data...
                    </div>
                    <div
                        style={{
                            width: "40px",
                            height: "40px",
                            border: "4px solid #e5e7eb",
                            borderTop: "4px solid #4f6ef7",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            margin: "0 auto",
                        }}
                    ></div>
                </div>
            </div>
        );
    }

    const apiErrorMessage = apiData?.error ? apiData?.message : null;

    return (
        <div className="MainTeamsData" style={{ marginTop: "50px" }}>
            <div className="tp">
                <div
                    className="tp__body"
                    style={{ margin: window.innerWidth > 1800 ? "0 60px" : "0 auto" }}
                >
                    <div className="tp__filters">
                        <div className="tp__filter-group">
                            <DateFilterSelect
                                value={[commonFilter.fromDate, commonFilter.toDate]}
                                onChange={(range) => {
                                    handleDateFilterPopup("fromDate", range[0]);
                                    handleDateFilterPopup("toDate", range[1]);
                                }}
                                isFilterCleared={false}
                            />
                            <select
                                className="tp__member-select"
                                style={{
                                    border: "1px solid rgb(204, 204, 204)",
                                    padding: "5px 12px",
                                }}
                                value={selectedMemeber}
                                onChange={(e)=>setselectedMemeber(e.target.value)}
                            >
                                <option value={""}>Select Team Members</option>
                                {memberName && memberName?.length > 0 && memberName?.map((manager, index) => (
        <optgroup
            key={index}
            label={manager?.managerName}
        >
            {/* Manager selectable */}
            <option value={manager?.managerEmail}>
                {manager?.managerName} (Manager)
            </option>

            {/* Employees */}
            {manager?.employees?.map((emp) => (
                <option
                    key={emp?.employeeId}
                    value={emp?.employeeEmail}
                >
                    {emp?.employeeName}
                </option>
            ))}
        </optgroup>
    ))}
                            </select>
                           

                            {/* Advanced Filters Button */}
                            <button
                                className={`tp__adv-btn${isAdvancedModified ? " tp__adv-btn--active" : ""}`}
                                onClick={() => setShowAdvanced((v) => !v)}
                            >
                                <SlidersIcon />
                                Advanced Filters
                                {isAdvancedModified && <span className="tp__adv-dot" />}
                            </button>
                        </div>
                    </div>

                    {/* Advanced Filters Panel */}
                    {showAdvanced && (
                        <div className="tp__adv-panel" style={{ marginTop: "15px" }}>
                            <div className="tp__adv-panel-hd">
                                <span className="tp__adv-panel-title">Advanced Filters</span>
                                <button className="tp__adv-reset" onClick={handleResetAdvanced}>
                                    <RefreshIcon style={{ width: "auto" }} /> Reset to defaults
                                </button>
                            </div>
                            <div className="tp__adv-grid">
                                {ADVANCED_FILTER_CONFIG?.map(({ key, label, type, step }) => (
                                    <div key={key} className="tp__adv-field">
                                        <label className="tp__adv-label">{label}</label>
                                        <input
                                            className="tp__adv-input"
                                            type={type}
                                            step={step}
                                            value={advancedDraft[key]}
                                            onChange={(e) =>
                                                handleAdvancedChange(key, e.target.value)
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="tp__adv-actions">
                                <button
                                    className="tp__adv-cancel"
                                    onClick={() => {
                                        setAdvancedDraft({ ...appliedAdvanced });
                                        setShowAdvanced(false);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button className="tp__adv-apply" onClick={handleApplyAdvanced}>
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    )}

                    {/* {apiErrorMessage && (
                        <div
                            style={{
                                margin: "12px 0 18px",
                                padding: "12px 14px",
                                borderRadius: 10,
                                background: "#fffbeb",
                                border: "1px solid #fde68a",
                                color: "#92400e",
                                fontSize: 14,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                            }}
                        >
                            <div style={{ lineHeight: 1.35 }}>
                                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                                    Some data could not be loaded
                                </div>
                                <div>{apiErrorMessage}</div>
                            </div>
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    padding: "8px 12px",
                                    background: "#92400e",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    )} */}

                    {/* <div className="tp__stats">
            <div className="tp__stat-card">
              <div className="tp__stat-content">
                <label>TOTAL LEADS</label>
                <div className="val">{totals?.totalLeads}</div>
              </div>
              <div className="tp__stat-icon tp__stat-icon--blue">
                <UsersIcon />
              </div>
            </div>
            <div className="tp__stat-card">
              <div className="tp__stat-content">
                <label>APPROVALS</label>
                <div className="val">{totals?.totalApproval}</div>
              </div>
              <div className="tp__stat-icon tp__stat-icon--teal">
                <CheckIcon />
              </div>
            </div>
            <div className="tp__stat-card">
              <div className="tp__stat-content">
                <label>DISBURSED (FRESH + REPEAT)</label>
                <div className="val">
                  {totals?.totalFresh + totals?.totalRepeat}
                </div>
               
              </div>
              <div className="tp__stat-icon tp__stat-icon--green">
                <CheckIcon />
              </div>
            </div>
            <div className="tp__stat-card">
              <div className="tp__stat-content">
                <label>TOTAL AMOUNT</label>
                <div className="val">{formatINR(totals?.totalAmount)}</div>
              </div>
              <div className="tp__stat-icon tp__stat-icon--orange">
                <RupeeIcon />
              </div>
            </div>
          </div> */}

                    <div
                        className="tp__stats"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "16px",
                        }}
                    >
                        {/* Projected Fresh */}
                        <div className="tp__stat-card">
                            <div className="tp__stat-content">
                                <label>DISBURSED PROJECTED FRESH</label>
                                <div className="val">{totals?.projectedFreshDisbursed}</div>
                            </div>

                            <div className="tp__stat-icon tp__stat-icon--blue">
                                <UsersIcon />
                            </div>
                        </div>

                        {/* Projected Repeat */}
                        <div className="tp__stat-card">
                            <div className="tp__stat-content">
                                <label>DISBURSED PROJECTED REPEAT</label>
                                <div className="val">{totals?.projectedRepeatDisbursed}</div>
                            </div>

                            <div className="tp__stat-icon tp__stat-icon--teal">
                                <CheckIcon />
                            </div>
                        </div>

                        {/* Projected Total */}
                        <div className="tp__stat-card">
                            <div className="tp__stat-content">
                                <label>DISBURSED PROJECTED TOTAL</label>
                                <div className="val">{totals?.projectedTotalDisbursed}</div>
                            </div>

                            <div className="tp__stat-icon tp__stat-icon--green">
                                <CheckIcon />
                            </div>
                        </div>

                        {/* Actual Fresh */}
                        <div className="tp__stat-card">
                            <div className="tp__stat-content">
                                <label>DISBURSED ACTUAL FRESH</label>
                                <div className="val">{totals?.actualFreshDisbursed}</div>
                            </div>

                            <div className="tp__stat-icon tp__stat-icon--orange">
                                <UsersIcon />
                            </div>
                        </div>

                        {/* Actual Repeat */}
                        <div className="tp__stat-card">
                            <div className="tp__stat-content">
                                <label>DISBURSED ACTUAL REPEAT</label>
                                <div className="val">{totals?.actualRepeatDisbursed}</div>
                            </div>

                            <div className="tp__stat-icon tp__stat-icon--blue">
                                <CheckIcon />
                            </div>
                        </div>

                        {/* Actual Total */}
                        <div className="tp__stat-card">
                            <div className="tp__stat-content">
                                <label>DISBURSED ACTUAL TOTAL</label>
                                <div className="val">{totals?.actualTotalDisbursed}</div>
                            </div>

                            <div className="tp__stat-icon tp__stat-icon--green">
                                <RupeeIcon />
                            </div>
                        </div>
                    </div>

                    {/* <div className="tp__charts">
            <div className="tp__chart-card">
              <h3>Leads vs Approvals</h3>
              <p>Monthly breakdown</p>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={chartData} barCategoryGap="32%" barGap={3}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0f0f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<BarTooltip />}
                    cursor={{ fill: "rgba(79,110,247,0.04)" }}
                  />
                  <Bar dataKey="Leads" fill="#4f6ef7" radius={[3, 3, 0, 0]} />
                  <Bar
                    dataKey="Approvals"
                    fill="#22d3c5"
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="tp__chart-legend">
                <span className="tp__legend-item">
                  <i className="dot dot--blue" /> Leads
                </span>
                <span className="tp__legend-item">
                  <i className="dot dot--teal" /> Approvals
                </span>
              </div>
            </div>

            <div className="tp__chart-card">
              <h3>Disbursed Amount</h3>
              <p>Trend</p>
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="amtGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4f6ef7" stopOpacity={0.2} />
                      <stop
                        offset="95%"
                        stopColor="#4f6ef7"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                    <linearGradient id="famtGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                      <stop
                        offset="95%"
                        stopColor="#10b981"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                    <linearGradient id="ramtGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                      <stop
                        offset="95%"
                        stopColor="#f59e0b"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0f0f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<AreaTooltip />} />

          
                  <Area
                    type="monotone"
                    dataKey="TotalDisburseamount"
                    stroke="#4f6ef7"
                    strokeWidth={2.5}
                    fill="url(#amtGrad)"
                    dot={false}
                    activeDot={{ r: 5, fill: "#4f6ef7", strokeWidth: 0 }}
                  />

                 
                  <Area
                    type="monotone"
                    dataKey="FreshDisburseamount"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#famtGrad)"
                    dot={false}
                    activeDot={{ r: 5, fill: "#10b981", strokeWidth: 0 }}
                  />

            
                  <Area
                    type="monotone"
                    dataKey="RepeatDisburseamount"
                    stroke="#f59e0b"
                    strokeWidth={2.5}
                    fill="url(#ramtGrad)"
                    dot={false}
                    activeDot={{ r: 5, fill: "#f59e0b", strokeWidth: 0 }}
                  />
           
                  <Area
                    dataKey="TotalDisbursecount"
                    hide
                    stroke="none"
                    fill="none"
                    legendType="none"
                  />
                  <Area
                    dataKey="FreshDisbursecount"
                    hide
                    stroke="none"
                    fill="none"
                    legendType="none"
                  />
                  <Area
                    dataKey="RepeatDisbursecount"
                    hide
                    stroke="none"
                    fill="none"
                    legendType="none"
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="tp__chart-legend">
                <span className="tp__legend-item">
                  <i
                    className="dot dot--blue"
                    style={{ background: "#4f6ef7" }}
                  />{" "}
                  Total Amount
                </span>
                <span className="tp__legend-item">
                  <i
                    className="dot dot--teal"
                    style={{ background: "#10b981" }}
                  />{" "}
                  Fresh Amount
                </span>
                <span className="tp__legend-item">
                  <i
                    className="dot dot--teal"
                    style={{ background: "#f59e0b" }}
                  />{" "}
                  Repeat Amount
                </span>
              </div>
            </div>
          </div> */}

                    <div className="tp__charts">
                        {/* BAR CHART */}
                        <div className="tp__chart-card">
                            <h3>Projected vs Actual Disbursed</h3>
                            <p>Monthly breakdown</p>

                            <ResponsiveContainer width="100%" height={210}>
                                <BarChart data={chartData} barCategoryGap="32%" barGap={3}>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f0f0f0"
                                        vertical={false}
                                    />

                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 11, fill: "#9ca3af" }}
                                        axisLine={false}
                                        tickLine={false}
                                    />

                                    <YAxis
                                        tick={{ fontSize: 11, fill: "#9ca3af" }}
                                        axisLine={false}
                                        tickLine={false}
                                    />

                                    <Tooltip
                                        content={<BarTooltip />}
                                        cursor={{ fill: "rgba(79,110,247,0.04)" }}
                                    />

                                    {/* PROJECTED */}
                                    <Bar
                                        dataKey="projectedFreshDisbursed"
                                        stackId="projected"
                                        fill="#4f6ef7"
                                        name="Projected Fresh"
                                    />

                                    <Bar
                                        dataKey="projectedRepeatDisbursed"
                                        stackId="projected"
                                        fill="#10b981"
                                        name="Projected Repeat"
                                    />

                                    <Bar
                                        dataKey="projectedTotalDisbursed"
                                        stackId="projected"
                                        fill="#f59e0b"
                                        name="Projected Total"
                                        radius={[3, 3, 0, 0]}
                                    />

                                    {/* ACTUAL */}
                                    <Bar
                                        dataKey="actualFreshDisbursed"
                                        stackId="actual"
                                        fill="#7c3aed"
                                        name="Actual Fresh"
                                    />

                                    <Bar
                                        dataKey="actualRepeatDisbursed"
                                        stackId="actual"
                                        fill="#ec4899"
                                        name="Actual Repeat"
                                    />

                                    <Bar
                                        dataKey="actualTotalDisbursed"
                                        stackId="actual"
                                        fill="#14b8a6"
                                        name="Actual Total"
                                        radius={[3, 3, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>

                            <div className="tp__chart-legend">
                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#4f6ef7" }} />{" "}
                                    Projected Fresh
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#10b981" }} />{" "}
                                    Projected Repeat
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#f59e0b" }} />{" "}
                                    Projected Total
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#7c3aed" }} /> Actual
                                    Fresh
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#ec4899" }} /> Actual
                                    Repeat
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#14b8a6" }} /> Actual
                                    Total
                                </span>
                            </div>
                        </div>

                        {/* AREA CHART */}
                        <div className="tp__chart-card">
                            <h3>Disbursed Trend</h3>
                            <p>Projected vs Actual</p>

                            <ResponsiveContainer width="100%" height={210}>
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="pfGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#4f6ef7" stopOpacity={0.2} />
                                            <stop
                                                offset="95%"
                                                stopColor="#4f6ef7"
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>

                                        <linearGradient id="prGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                                            <stop
                                                offset="95%"
                                                stopColor="#10b981"
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>

                                        <linearGradient id="ptGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                                            <stop
                                                offset="95%"
                                                stopColor="#f59e0b"
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>

                                        <linearGradient id="afGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.2} />
                                            <stop
                                                offset="95%"
                                                stopColor="#7c3aed"
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>

                                        <linearGradient id="arGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ec4899" stopOpacity={0.2} />
                                            <stop
                                                offset="95%"
                                                stopColor="#ec4899"
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>

                                        <linearGradient id="atGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.2} />
                                            <stop
                                                offset="95%"
                                                stopColor="#14b8a6"
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f0f0f0"
                                        vertical={false}
                                    />

                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 11, fill: "#9ca3af" }}
                                        axisLine={false}
                                        tickLine={false}
                                    />

                                    <YAxis
                                        tick={{ fontSize: 11, fill: "#9ca3af" }}
                                        axisLine={false}
                                        tickLine={false}
                                    />

                                    <Tooltip content={<AreaTooltip />} />

                                    {/* PROJECTED */}
                                    <Area
                                        type="monotone"
                                        dataKey="projectedFreshDisbursedAmount"
                                        stroke="#4f6ef7"
                                        strokeWidth={2.5}
                                        fill="url(#pfGrad)"
                                        dot={false}
                                    />

                                    <Area
                                        type="monotone"
                                        dataKey="projectedRepeatDisbursedAmount"
                                        stroke="#10b981"
                                        strokeWidth={2.5}
                                        fill="url(#prGrad)"
                                        dot={false}
                                    />

                                    <Area
                                        type="monotone"
                                        dataKey="projectedTotalDisbursedAmount"
                                        stroke="#f59e0b"
                                        strokeWidth={2.5}
                                        fill="url(#ptGrad)"
                                        dot={false}
                                    />

                                    {/* ACTUAL */}
                                    <Area
                                        type="monotone"
                                        dataKey="actualFreshDisbursedAmount"
                                        stroke="#7c3aed"
                                        strokeWidth={2.5}
                                        fill="url(#afGrad)"
                                        dot={false}
                                    />

                                    <Area
                                        type="monotone"
                                        dataKey="actualRepeatDisbursedAmount"
                                        stroke="#ec4899"
                                        strokeWidth={2.5}
                                        fill="url(#arGrad)"
                                        dot={false}
                                    />

                                    <Area
                                        type="monotone"
                                        dataKey="actualTotalDisbursedAmount"
                                        stroke="#14b8a6"
                                        strokeWidth={2.5}
                                        fill="url(#atGrad)"
                                        dot={false}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>

                            <div className="tp__chart-legend">
                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#4f6ef7" }} />{" "}
                                    Projected Fresh
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#10b981" }} />{" "}
                                    Projected Repeat
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#f59e0b" }} />{" "}
                                    Projected Total
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#7c3aed" }} /> Actual
                                    Fresh
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#ec4899" }} /> Actual
                                    Repeat
                                </span>

                                <span className="tp__legend-item">
                                    <i className="dot" style={{ background: "#14b8a6" }} /> Actual
                                    Total
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="tp__tabs">
                        {[
                            ["members", "Team members"],
                            ["monthly", "Monthly breakdown"],
                        ].map(([key, label]) => (
                            <button
                                key={key}
                                className={`tp__tab${activeTab === key ? " tp__tab--active" : ""}`}
                                onClick={() => setActiveTab(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {activeTab === "members" && (
                        <div className="tp__members-card">
                            <div className="tp__members-card-hd">
                                <h3>Member contribution</h3>
                                <p>Aggregated leads handled in selected period</p>
                            </div>
                            {console.log("members:",members)}
                            {members?.map((m) => (
                                <div key={m?.email} className="tp__member-row" style={{ flexDirection: 'column', alignItems: 'normal' }}>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', width: '100%' }}>
                                        <div className={`tp__avatar tp__avatar--${m.color}`}>
                                            {m.initials}
                                        </div>
                                        <div className="tp__member-body">
                                            <div className="tp__member-top">
                                                <span className="tp__member-name">{m.name}</span>
                                                <span
                                                    className={`tp__badge tp__badge--${m.badge.toLowerCase()}`}
                                                >
                                                    {m?.badge}
                                                </span>
                                            </div>
                                            <div className="tp__member-meta">
                                                ✉ {m.email} · Lead: {m.managerName}
                                            </div>
                                            <div style={{ display: 'flex',alignItems:"center",gap:'15px' }}>
                                                <div className="tp__bar" style={{width:'100%'}}>
                                                    <div
                                                        className="tp__bar-fill"
                                                        style={{ width: `${m?.barPct}%`,
                                                        background: m?.barPct < 25 ? "red" : m?.barPct >= 25 && m?.barPct < 50 ? "#ffc200" : m?.barPct >= 50 && m?.barPct < 75 ? "#267dff" : "linear-gradient(90deg, #4f6ef7, #6366f1)"
                                                    }}

                                                    />
                                                </div>
                                                <h5 style={{margin:'0'}}>{m?.barPct}%</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tp__member-leads">
                                        <div className="tp__member-metrics" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
                                            <div className="metric">
                                                <span className="k">Projected Assigned</span>
                                                <span className="v">{m?.projectedLeads ?? 0}</span>
                                            </div>
                                            <div className="metric">
                                                <span className="k">Actual Assigned</span>
                                                <span className="v">{m?.leads ?? 0}</span>
                                            </div>
                                            <div className="metric metric--ok">
                                                <span className="k">{m?.badge == "Credit" ? "Approved" :"Completed"}</span>
                                                <span className="v">{m?.completed ?? 0}</span>
                                            </div>
                                            <div className="metric metric--warn">
                                                <span className="k">Pending</span>
                                                <span className="v">{m?.pending ?? 0}</span>
                                            </div>
                                            <div className="metric metric--ok" style={{ color: '#10b981' }}>
                                                <span className="k">Disbursed</span>
                                                <span className="v">{m?.disbursedLeads ?? 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === "monthly" && (
                        <div className="tp__monthly">
                            <div className="tp__members-card-hd">
                                <h3>Monthly Breakdown</h3>

                            </div>
                            {Array.isArray(apiData) &&
                                apiData?.map((d) => {
                                    const monthKey = normalizeMonth(d?.month);
                                    const isOpen = openMonth === monthKey;
                                    const totalDisbursed =
                                        Number(d?.actualDisbursalData?.freshDisbursedCount || 0) +
                                        Number(d?.actualDisbursalData?.repeatDisbursedCount || 0);
                                    return (
                                        <div
                                            key={monthKey || d?.month}
                                            className={`tp__month-block${isOpen ? " tp__month-block--open" : ""}`}
                                        >
                                            <div
                                                className="tp__month-row"
                                                onClick={() => setOpenMonth(isOpen ? null : monthKey)}
                                            >
                                                <div className="tp__month-badge">
                                                    {monthLabel(d?.month)}
                                                </div>
                                                <div
                                                    className="tp__month-stats"
                                                    style={{ justifyContent: "space-evenly" }}
                                                >
                                                    {/* <div className="tp__month-stat">
                                                        <span className="lbl">LEADS</span>
                                                        <span className="val">{d.totalLeadsPerDay}</span>
                                                    </div>
                                                    <div className="tp__month-stat">
                                                        <span className="lbl">APPROVALS</span>
                                                        <span className="val">
                                                            {d.totalApprovalsPerDay}
                                                        </span>
                                                    </div> */}
                                                    <div className="tp__month-stat">
                                                        <span className="lbl">DISBURSED COUNT</span>
                                                        <span className="val">{totalDisbursed}</span>
                                                    </div>
                                                    <div className="tp__month-stat">
                                                        <span className="lbl">DISBURSED AMOUNT</span>
                                                        <span className="val">
                                                            {formatINR(d?.actualDisbursalData?.totalDisbursedAmount)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="tp__month-chev">
                                                    <ChevronIcon open={isOpen} />
                                                </div>
                                            </div>

                                            {isOpen && (
                                                <div className="tp__month-detail">
                                                    <div className="tp__pills">
                                                        <div className="tp__pill tp__pill--blue" style={{ width: "24.3%" }}>
                                                            <span className="pill-lbl">Fresh Amount</span>
                                                            <span className="pill-val">
                                                                {formatINR(d?.actualDisbursalData?.freshDisbursedAmount)}
                                                            </span>
                                                        </div>
                                                        <div className="tp__pill tp__pill--purple" style={{ width: "24.3%" }}>
                                                            <span className="pill-lbl">Repeat Amount</span>
                                                            <span className="pill-val">
                                                                {formatINR(d?.actualDisbursalData?.repeatDisbursedAmount)}
                                                            </span>
                                                        </div>
                                                        <div className="tp__pill tp__pill--green" style={{ width: "24.3%" }}>
                                                            <span className="pill-lbl">Fresh Disbursed</span>
                                                            <span className="pill-val">
                                                                {d?.actualDisbursalData?.freshDisbursedCount}
                                                            </span>
                                                        </div>
                                                        <div className="tp__pill tp__pill--orange" style={{ width: "24.3%" }}>
                                                            <span className="pill-lbl">Repeat Disbursed</span>
                                                            <span className="pill-val">
                                                                {d?.actualDisbursalData?.repeatDisbursedCount}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="tp__chart-legend" style={{ justifyContent: 'end', marginBottom: '10px' }}>
                                                        <span className="tp__legend-item">
                                                            <i className="dot" style={{ background: "#3B82F6" }} />{" "}
                                                            Projected Leads
                                                        </span>

                                                        <span className="tp__legend-item">
                                                            <i className="dot" style={{ background: "#8B5CF6" }} />{" "}
                                                            Actual Leads
                                                        </span>

                                                        <span className="tp__legend-item">
                                                            <i className="dot" style={{ background: "#22C55E" }} />{" "}
                                                            Completed / Disbursed Leads
                                                        </span>

                                                        <span className="tp__legend-item">
                                                            <i className="dot" style={{ background: "#F59E0B" }} />
                                                            Pending Leads
                                                        </span>


                                                    </div>

                                                    <div className="tp__alloc-grid">
                                                        {Object.keys(ALLOCATION_CONFIG).map((key) => {
                                                            const allocationData = d?.[key];

                                                            if (!allocationData) return null;

                                                            return (
                                                                <AllocationSection
                                                                    key={key}
                                                                    allocationKey={key}
                                                                    allocationData={allocationData}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const style = document.createElement("style");
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default TeamPerformance;
